/**
 * Utility functions for parsing interactive HTML data attributes
 *
 * These functions work server-side with raw HTML strings.
 * We need to parse from the original HTML before Interweave processes it.
 */

import type {
  FaqConfig,
  FaqItemData,
  SliderConfig,
  SliderItemData,
  TabsConfig,
  TabData,
  ExpandableConfig,
  ModalConfig,
} from './types';

/**
 * Parse boolean from string attribute
 */
export const parseBoolean = (value: string | null | undefined, defaultValue = false): boolean => {
  if (value === null || value === undefined) return defaultValue;
  return value === 'true';
};

/**
 * Parse integer from string attribute
 */
export const parseInteger = (value: string | null | undefined, defaultValue = 0): number => {
  if (value === null || value === undefined) return defaultValue;
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Parse comma-separated list of integers
 */
export const parseIntegerList = (value: string | null | undefined): number[] => {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
};

/**
 * Parse Modal configuration from element
 */
export const parseModalConfig = (element: HTMLElement): ModalConfig => {
  return {
    id: element.getAttribute('data-modal-id') || undefined,
    title: element.getAttribute('data-modal-title') || undefined,
    size: (element.getAttribute('data-modal-size') as 'sm' | 'md' | 'lg' | 'xl') || 'xl',
  };
};

/**
 * Parse FAQ configuration from element
 * In SSR context with Interweave, we get a DOM-like node
 * We'll extract child elements directly rather than using innerHTML
 */
export const parseFaqConfig = (element: any): FaqConfig => {
  const mode = element.getAttribute('data-faq-mode') || 'accordion';
  const defaultOpenStr = element.getAttribute('data-faq-default-open');
  const defaultOpen = parseIntegerList(defaultOpenStr);
  const icon = element.getAttribute('data-faq-icon') || 'chevron';

  const items: FaqItemData[] = [];

  // Helper to recursively find an element with specific attribute
  const findByAttribute = (node: any, attrName: string): any => {
    if (!node) return null;
    if (node.nodeType === 1 && node.getAttribute && node.getAttribute(attrName) !== null) {
      return node;
    }
    const children = node.childNodes || node.children || [];
    for (const child of children) {
      const found = findByAttribute(child, attrName);
      if (found) return found;
    }
    return null;
  };

  // Helper to recursively find element with data-variable containing substring
  const findByVariableSubstring = (node: any, substring: string): any => {
    if (!node) return null;
    if (node.nodeType === 1 && node.getAttribute) {
      const dataVar = node.getAttribute('data-variable');
      if (dataVar && dataVar.includes(substring)) {
        return node;
      }
    }
    const children = node.childNodes || node.children || [];
    for (const child of children) {
      const found = findByVariableSubstring(child, substring);
      if (found) return found;
    }
    return null;
  };

  // Try to get child nodes - Interweave should provide these
  const childNodes = element.childNodes || element.children || [];

  // Find all FAQ item divs
  let index = 0;
  for (const child of childNodes) {
    if (child.nodeType === 1 && child.getAttribute && child.getAttribute('data-faq-item') !== null) {
      // This is a FAQ item
      let trigger = '';
      let content = '';

      // Find the trigger element
      const triggerNode = findByAttribute(child, 'data-faq-trigger');
      if (triggerNode) {
        // Serialize the ENTIRE trigger node (including wrapper with classes)
        trigger = nodeToHtml(triggerNode);
        // Fallback to textContent if serialization fails
        if (!trigger) {
          trigger = triggerNode.textContent || '';
        }
      }

      // Find the content element - first try data-faq-content
      let contentNode = findByAttribute(child, 'data-faq-content');

      // If not found, try data-variable with .answer
      if (!contentNode) {
        contentNode = findByVariableSubstring(child, '.answer');
      }

      if (contentNode) {
        // Serialize the ENTIRE content node (including wrapper with classes)
        content = nodeToHtml(contentNode);
        // Fallback to textContent if serialization fails
        if (!content) {
          content = contentNode.textContent || '';
        }
      }

      items.push({
        trigger: trigger.trim(),
        content: content.trim(),
        isDefaultOpen: mode === 'all-open' || defaultOpen.includes(index),
      });

      index++;
    }
  }

  return {
    mode: mode as any,
    defaultOpen,
    icon: icon as any,
    items,
  };
};

/**
 * Serialize a DOM node to HTML string recursively
 */
const nodeToHtml = (node: any): string => {
  if (!node) return '';

  // Text node
  if (node.nodeType === 3) {
    return node.textContent || '';
  }

  // Element node
  if (node.nodeType === 1) {
    const tagName = node.tagName.toLowerCase();
    let html = `<${tagName}`;

    // Add attributes
    if (node.attributes) {
      for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        html += ` ${attr.name}="${attr.value}"`;
      }
    }

    html += '>';

    // Add children
    const children = node.childNodes || node.children || [];
    for (const child of children) {
      html += nodeToHtml(child);
    }

    html += `</${tagName}>`;
    return html;
  }

  return '';
};

/**
 * Parse Slider configuration from element
 */
export const parseSliderConfig = (element: any): SliderConfig => {
  const autoPlay = parseBoolean(element.getAttribute('data-slider-auto-play'), false);
  const interval = parseInteger(element.getAttribute('data-slider-interval'), 5000);
  const showDots = parseBoolean(element.getAttribute('data-slider-show-dots'), true);
  const showArrows = parseBoolean(element.getAttribute('data-slider-show-arrows'), true);
  const loop = parseBoolean(element.getAttribute('data-slider-loop'), true);
  const effect = element.getAttribute('data-slider-effect') || 'slide';

  const items: SliderItemData[] = [];

  // Try to get child nodes
  const childNodes = element.childNodes || element.children || [];

  let index = 0;
  for (const child of childNodes) {
    if (child.nodeType === 1 && child.getAttribute && child.getAttribute('data-slider-item') !== null) {
      // This is a slider item - serialize its inner content to HTML
      const children = child.childNodes || child.children || [];
      let itemHtml = '';
      for (const grandchild of children) {
        itemHtml += nodeToHtml(grandchild);
      }

      items.push({
        html: itemHtml,
        index,
      });
      index++;
    }
  }

  return {
    autoPlay,
    interval,
    showDots,
    showArrows,
    loop,
    effect: effect as any,
    items,
  };
};

/**
 * Parse Tabs configuration from element
 */
export const parseTabsConfig = (element: HTMLElement): TabsConfig => {
  const defaultTab = parseInteger(element.getAttribute('data-tabs-default'), 0);
  const html = element.innerHTML;
  const tabs: TabData[] = [];

  // Extract tab triggers
  const triggerRegex = /<[^>]*data-tab-trigger="(\d+)"[^>]*>([\s\S]*?)<\/[^>]+>/g;
  let triggerMatch: RegExpExecArray | null;

  while ((triggerMatch = triggerRegex.exec(html)) !== null) {
    const tabIndex = parseInt(triggerMatch[1], 10);
    const triggerHtml = triggerMatch[2];

    // Find corresponding content
    const contentRegex = new RegExp(`<[^>]*data-tab-content="${tabIndex}"[^>]*>([\\s\\S]*?)</[^>]+>`, 'g');
    const contentMatch = contentRegex.exec(html);
    const contentHtml = contentMatch ? contentMatch[1] : '';

    tabs.push({
      index: tabIndex,
      triggerHtml,
      contentHtml,
    });
  }

  return {
    defaultTab,
    tabs,
  };
};

/**
 * Parse Expandable configuration from element
 */
export const parseExpandableConfig = (element: HTMLElement): ExpandableConfig => {
  const defaultOpen = element.getAttribute('data-expand-default') === 'open';
  const html = element.innerHTML;

  // Extract trigger
  const triggerMatch = html.match(/<[^>]*data-expand-trigger[^>]*>([\s\S]*?)<\/[^>]+>/);
  const triggerHtml = triggerMatch ? triggerMatch[1] : '';

  // Extract content
  const contentMatch = html.match(/<[^>]*data-expand-content[^>]*>([\s\S]*?)<\/[^>]+>/);
  const contentHtml = contentMatch ? contentMatch[1] : '';

  return {
    defaultOpen,
    triggerHtml,
    contentHtml,
  };
};
